import {
  Badge,
  chakra,
  Flex,
  HStack,
  MenuDivider,
  MenuItem,
  Text,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { usePrevious } from '@gamma/ui/hooks';
import DarkModeSharpIcon from '@gamma/ui/icons/DarkModeSharpIcon';
import DescriptionSharpIcon from '@gamma/ui/icons/DescriptionSharpIcon';
import LightModeSharpIcon from '@gamma/ui/icons/LightModeSharpIcon';
import OpenInNewSharpIcon from '@gamma/ui/icons/OpenInNewSharpIcon';
import PowerSettingsNewSharpIcon from '@gamma/ui/icons/PowerSettingsNewSharpIcon';
import ProgressActivitySharpIcon from '@gamma/ui/icons/ProgressActivitySharpIcon';
import SettingsSharpIcon from '@gamma/ui/icons/SettingsSharpIcon';
import { NavMenu, NavUserLabel, RouterLink } from '@gamma/ui/navigation';
import {
  isHumioThemeSupported,
  setHumioTheme,
} from '@investigator/features/investigations/queries';
import { ROUTES, URLS } from '@investigator/shared/constants';
import {
  useLogOperationCall,
  useTenantInfo,
  useUserSession,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { AuthContext, useHumioToken } from '@investigator/shared/providers';
import { getFormattedAlias } from '@investigator/shared/utils';
import { useCallback, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router';

const { accountSettings } = ROUTES;

/**
 * TopbarLinks component provides user menu with account settings,
 * theme toggle, documentation links, and sign out functionality
 */
export const TopbarLinks = () => {
  const { userAlias, authState, userRole } = useContext(AuthContext);
  const { signOut } = useUserSession();
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();

  // Use centralized token management instead of localStorage
  const { token: sessionId } = useHumioToken();

  const { humioUrlPrefix } = useTenantInfo();

  const prevColorMode = usePrevious(colorMode);

  /**
   * Synchronize app theme with Humio instance when color mode changes
   */
  useEffect(() => {
    const setHumioConfig = async () => {
      // Check if theme setting is supported (skip if we've already determined it's not)
      if (!isHumioThemeSupported()) {
        return;
      }

      // Don't try to set theme without a valid token
      if (!sessionId || sessionId === 'undefined') {
        return;
      }
      await setHumioTheme(humioUrlPrefix, colorMode, sessionId);
    };

    // Only set theme if color mode actually changed and we have a session
    if (
      sessionId &&
      colorMode !== prevColorMode &&
      prevColorMode !== undefined
    ) {
      setHumioConfig();
    }
  }, [colorMode, authState, sessionId, prevColorMode, humioUrlPrefix]);

  const colorModeIcon = useColorModeValue(
    <DarkModeSharpIcon />,
    <LightModeSharpIcon />,
  );
  const oppositeColorMode = useColorModeValue('Dark', 'Light');
  const logOperationCall = useLogOperationCall();

  const role = userRole ? i18n.roles[userRole] : undefined;

  /**
   * Handle user sign out
   * Logs the operation, clears session data, and redirects to login
   */
  const handleSignOut = useCallback(async () => {
    await logOperationCall('LOG_OUT', 'USER');
    await signOut();
    localStorage.removeItem('shouldShowGPTToastGeneralSettingsLink');
    // Clear the theme support flag on logout
    sessionStorage.removeItem('humio_theme_unsupported');
    navigate(ROUTES.login, { replace: true });
  }, [logOperationCall, signOut, navigate]);

  return (
    <NavMenu
      label={
        <NavUserLabel
          isAdmin={userRole === 'admin'}
          username={userAlias ? getFormattedAlias(userAlias, 15, 10) : ''}
        >
          {userRole && (
            <Badge
              variant="outline"
              colorScheme={
                userRole === 'admin'
                  ? 'purple'
                  : userRole === 'analyst'
                    ? 'orange'
                    : userRole === 'viewer'
                      ? 'gray'
                      : 'blue'
              }
              data-testid="gamma-nav-user-label-role"
            >
              {role}
            </Badge>
          )}
        </NavUserLabel>
      }
      isCaretHidden
    >
      <MenuItem as="div">
        <RouterLink
          to={`${accountSettings}`}
          data-testid="topbar-link-account-settings"
        >
          <HStack alignItems="center">
            <SettingsSharpIcon />
            <chakra.span>{i18n.navigation.account.settings}</chakra.span>
          </HStack>
        </RouterLink>
      </MenuItem>
      <MenuItem onClick={toggleColorMode} data-testid="topbar-link-color-mode">
        <HStack alignItems="center">
          {colorModeIcon}
          <chakra.span>
            {i18n.formatString(i18n.navigation.colorMode, oppositeColorMode)}
          </chakra.span>
        </HStack>
      </MenuItem>
      <MenuDivider />
      <MenuItem
        onClick={() => window.open(URLS.INVESTIGATOR_DOCS, '_blank')}
        data-testid="topbar-link-docs"
      >
        <HStack alignItems="center" w="full">
          <DescriptionSharpIcon />
          <Flex justifyContent="space-between" alignItems="center" flex="1">
            <Text textStyle="body-md">{i18n.navigation.docs}</Text>
            <OpenInNewSharpIcon size="sm" />
          </Flex>
        </HStack>
      </MenuItem>
      <MenuItem
        onClick={() => window.open(URLS.CORELIGHT_SUPPORT, '_blank')}
        data-testid="topbar-link-support"
      >
        <HStack alignItems="center" w="full">
          <ProgressActivitySharpIcon boxSize={5} />
          <Flex justifyContent="space-between" alignItems="center" flex="1">
            <Text textStyle="body-md">{i18n.navigation.corelightSupport}</Text>
            <OpenInNewSharpIcon size="sm" />
          </Flex>
        </HStack>
      </MenuItem>
      <MenuDivider />
      <MenuItem onClick={handleSignOut} data-testid="topbar-link-logout">
        <HStack alignItems="center">
          <PowerSettingsNewSharpIcon color="red.400" />
          <chakra.span>{i18n.authentication.logout}</chakra.span>
        </HStack>
      </MenuItem>
    </NavMenu>
  );
};
